import React, { useEffect, useState } from 'react';
import {
    Checkbox,
    Divider,
    Label,
    Modal,
    SparkIcon,
    SparkModalPortal,
    Text,
    TooltipTrigger,
} from '@spark';
import { isGenerativeTask } from '@/shared/domain/taskTypes';
import { extractExtraParamsFromPrompt } from '@/features/datasets/components/DataSelection/ExtraParamsUtils';
import { fetchModelDetails } from '@/features/trainings';
import { isTeacherComparisonEnabled, shouldShowEvaluationNormalization } from '@/features/trainings/utils/trainingUtils';
import EvaluationNormalizationSummary from '@/features/trainings/components/EvaluationNormalizationSummary';
import { formatDatasetNameList } from '@/features/datasets/utils/datasetCloneNames';
import {
    ComparisonSwitch,
    JsonSchemaInput,
    PromptInput,
    ResponseInterpreterSection,
    SystemPromptInput,
    TeacherJsonSchemaField,
    formatComparingAgainstStatus,
    getEnabledJsonSchemaError,
    resolveStudentFields,
    showInvalidJsonSchemaToast,
    useParserCodeState,
    useTeacherSamplePreview,
} from '@/features/model-setup';
import { useCommercialModels } from '@/shared/hooks/useCommercialModels';
import '@/features/model-setup/components/ModelSetupPage.css';

const STACK_STYLE = {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--dimension-spacing-24)',
};

const CHECKBOX_ROW_STYLE = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--dimension-spacing-08)',
};

function uniqueNames(values) {
    return [...new Set((values || []).filter(Boolean).map(String))];
}

function RecalculateComparePreview({ usecase }) {
    const [responseType, setResponseType] = useState('json');
    const {
        requestExample,
        setRequestExample,
        rawResponse,
        setRawResponse,
        extraParamsValues,
        setExtraParamsValues,
        shuffleLoading,
        generatingResponse,
        runValidationError,
        rawResponseLoading,
        handleShuffleExample,
        generateResponse,
        batchNotice,
    } = useTeacherSamplePreview({
        usecase,
        teacher: usecase?.teacher_model,
        provider: usecase?.teacher_provider || 'openai',
        prompt: usecase?.prompt,
        systemPrompt: usecase?.teacher_system_prompt,
        teacherSchema: usecase?.teacher_schema,
        autoload: true,
    });
    const {
        parserSelectionsFromCode,
    } = useParserCodeState({ usecase, setResponseType });

    return (
        <ResponseInterpreterSection
            requestExample={requestExample}
            setRequestExample={setRequestExample}
            onShuffleExample={handleShuffleExample}
            shuffleLoading={shuffleLoading}
            responseType={responseType}
            setResponseType={setResponseType}
            rawResponse={rawResponse}
            setRawResponse={setRawResponse}
            onGenerateResponse={generateResponse}
            generatingResponse={generatingResponse}
            runValidationError={runValidationError}
            onInspectLogic={() => { }}
            extraParamsOptions={{}}
            extraParamsValues={extraParamsValues}
            onExtraParamsValuesChange={setExtraParamsValues}
            promptExtraParams={extractExtraParamsFromPrompt(usecase?.prompt || '')}
            rawResponseLoading={rawResponseLoading}
            rawResponseError={null}
            validationResult={null}
            taskType={usecase?.task_type}
            isMultilabel={usecase?.is_multilabel}
            onParserPrefillChange={() => { }}
            initialParserSelections={parserSelectionsFromCode}
            userCode=""
            onUserCodeChange={() => { }}
            onValidate={() => { }}
            validationLoading={false}
            hideToolbar
            mappingReadOnly
            batchNotice={batchNotice}
        />
    );
}

function DatasetsUsedSection({ usecase }) {
    const benchmarks = uniqueNames(usecase?.benchmarks);
    return (
        <section className="recalc-eval-datasets fs-unmask" aria-label="Datasets Used">
            <Label label="Benchmarks Used" showAsterisk={false} showIcon={false} />
            <div className="recalc-eval-datasets__rows">
                <Text variant="detail-regular">
                    {formatDatasetNameList(benchmarks) || '-'}
                </Text>
            </div>
        </section>
    );
}

function TeacherPromptsRow({ usecase, availableProviders }) {
    const schema = (usecase?.teacher_schema || '').trim();
    return (
        <section className="model-setup-base-section" aria-label="Teacher prompts">
            <div className="model-setup-base-section__form">
                <SystemPromptInput
                    value={usecase?.teacher_system_prompt || ''}
                    onChange={() => { }}
                    role="Viewer"
                    rows={5}
                />
                <PromptInput
                    prompt={usecase?.prompt || ''}
                    onPromptChange={() => { }}
                    role="Viewer"
                />
                {schema ? (
                    <TeacherJsonSchemaField
                        provider={usecase?.teacher_provider}
                        modelId={usecase?.teacher_model}
                        availableProviders={availableProviders}
                        value={schema}
                        onChange={() => { }}
                        role="Viewer"
                        showEnableToggle={false}
                    />
                ) : null}
            </div>
        </section>
    );
}

function StudentPromptsRow({ usecase, availableProviders }) {
    const { system, user, schema } = resolveStudentFields(usecase, { availableProviders });
    return (
        <section className="model-setup-base-section" aria-label="Student prompts">
            <div className="model-setup-base-section__form">
                <SystemPromptInput
                    value={system}
                    onChange={() => { }}
                    role="Viewer"
                    labelText="System prompt"
                    rows={5}
                />
                <PromptInput
                    prompt={user}
                    onPromptChange={() => { }}
                    role="Viewer"
                    labelText="User prompt"
                />
                {schema ? (
                    <JsonSchemaInput
                        value={schema}
                        onChange={() => { }}
                        role="Viewer"
                        showEnableToggle={false}
                    />
                ) : null}
            </div>
        </section>
    );
}

function GenerativeStudentSection({
    usecase,
    availableProviders,
    includeInstructionInTraining,
    onIncludeInstructionInTrainingChange,
    studentFlagReady,
}) {
    if (!studentFlagReady) {
        return null;
    }

    return (
        <>
            <Divider />
            <section className="fs-unmask" aria-label="Student prompt inclusion">
                <div style={CHECKBOX_ROW_STYLE}>
                    <Checkbox
                        label="Include student prompt in evaluation"
                        status={includeInstructionInTraining ? 'active' : 'inactive'}
                        onChange={() => onIncludeInstructionInTrainingChange(!includeInstructionInTraining)}
                    />
                    <TooltipTrigger text="When enabled, the student system and user prompts from Model Configuration are included in every evaluation sample.">
                        <SparkIcon slug="circle-info" size="small" />
                    </TooltipTrigger>
                </div>
            </section>
            {includeInstructionInTraining ? (
                <>
                    <StudentPromptsRow usecase={usecase} availableProviders={availableProviders} />
                    <Divider />
                </>
            ) : null}
        </>
    );
}

/**
 * View-only confirmation of the parameters a re-evaluation will run with.
 * Confirm starts recalculation; Cancel / close leaves the current results.
 * Shuffle / Run in the compare preview call the teacher live and do not persist.
 * Model Returns mapping (JSON tokens, field picks) is view-only so the parser
 * function is not changed; Run still fills the sample output.
 */
const RecalculateEvaluationParamsModal = ({
    open,
    usecase,
    distillationEventId = null,
    onCancel,
    onConfirm,
    confirming = false,
    onGoToModelConfiguration,
}) => {
    const teacherComparisonEnabled = isTeacherComparisonEnabled(usecase);
    const isGenerative = isGenerativeTask(usecase?.task_type);
    const { availableProviders } = useCommercialModels();
    const [includeInstructionInTraining, setIncludeInstructionInTraining] = useState(false);
    const [studentFlagReady, setStudentFlagReady] = useState(!isGenerative);

    useEffect(() => {
        if (!open || !isGenerative) {
            setIncludeInstructionInTraining(false);
            setStudentFlagReady(true);
            return undefined;
        }
        if (!distillationEventId || !usecase?.client_id) {
            setIncludeInstructionInTraining(false);
            setStudentFlagReady(true);
            return undefined;
        }

        let cancelled = false;
        setStudentFlagReady(false);
        fetchModelDetails({
            client_id: usecase.client_id,
            distillation_event_id: distillationEventId,
        }).then((resp) => {
            if (cancelled) return;
            setIncludeInstructionInTraining(Boolean(
                resp?.distillation_event?.include_instruction_in_training,
            ));
            setStudentFlagReady(true);
        }).catch(() => {
            if (cancelled) return;
            setIncludeInstructionInTraining(false);
            setStudentFlagReady(true);
        });

        return () => {
            cancelled = true;
        };
    }, [open, isGenerative, distillationEventId, usecase?.client_id]);

    const handleConfirm = () => {
        if (confirming) return;
        if (getEnabledJsonSchemaError(usecase)) {
            showInvalidJsonSchemaToast({
                onGoToModelConfiguration: () => {
                    onCancel?.();
                    onGoToModelConfiguration?.();
                },
            });
            return;
        }
        onConfirm({
            includeInstructionInTraining: isGenerative ? includeInstructionInTraining : undefined,
        });
    };

    const studentSectionEndsWithDivider = isGenerative
        && studentFlagReady
        && includeInstructionInTraining;
    const showWhenEvaluating = shouldShowEvaluationNormalization(
        usecase,
        isGenerative ? (studentFlagReady ? includeInstructionInTraining : false) : undefined,
    );

    return (
        <SparkModalPortal open={open} onClose={onCancel} size="wide" verticalAlign="top">
            <Modal
                className="fs-unmask"
                title="Evaluate New Version"
                subtitle="Review the parameters this re-evaluation will run with. These values come from the current task configuration."
                feedbackType="info"
                showSubtitle
                showScrollBar
                showFooter
                showOverlay={false}
                primaryLabel="Evaluate New Version"
                tertiaryLabel="Cancel"
                primaryDisabled={confirming}
                primaryDisableWhenReadOnly
                onPrimaryClick={handleConfirm}
                onTertiaryClick={onCancel}
                onCloseClick={onCancel}
            >
                <div className="fs-unmask" style={STACK_STYLE}>
                    <DatasetsUsedSection usecase={usecase} />

                    {showWhenEvaluating ? (
                        <EvaluationNormalizationSummary source={usecase} variant="recalculate" />
                    ) : null}

                    {isGenerative ? (
                        <GenerativeStudentSection
                            usecase={usecase}
                            availableProviders={availableProviders}
                            includeInstructionInTraining={includeInstructionInTraining}
                            onIncludeInstructionInTrainingChange={setIncludeInstructionInTraining}
                            studentFlagReady={studentFlagReady}
                        />
                    ) : null}

                    {teacherComparisonEnabled ? (
                        <>
                            {!studentSectionEndsWithDivider ? <Divider /> : null}
                            <section
                                className="model-setup-compare-section model-setup-compare-section--status-only"
                                aria-label="Teacher comparison"
                            >
                                <ComparisonSwitch
                                    showResponseParser
                                    readOnly
                                    role="Viewer"
                                    hideIntro
                                    statusLabel={formatComparingAgainstStatus({
                                        teacherModel: usecase?.teacher_model,
                                        teacherProvider: usecase?.teacher_provider,
                                    })}
                                >
                                    <TeacherPromptsRow
                                        usecase={usecase}
                                        availableProviders={availableProviders}
                                    />
                                    <RecalculateComparePreview usecase={usecase} />
                                </ComparisonSwitch>
                            </section>
                        </>
                    ) : (
                        <>
                            {!studentSectionEndsWithDivider ? <Divider /> : null}
                            <Label
                                label="Comparing against commercial model is not active"
                                showAsterisk={false}
                                showIcon={false}
                            />
                        </>
                    )}
                </div>
            </Modal>
        </SparkModalPortal>
    );
};

export default RecalculateEvaluationParamsModal;
